import {
  Button,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  Spinner,
  Select as ChakraSelect,
  ModalFooter,
  Icon,
} from "@chakra-ui/react";
import { Layout } from "../../components/Layout/Layout";
import { useAppDispatch, useAppSelector } from "../../store";
import { useParams, Link as RouterLink } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { addRecruiter, fetchSingleDepartment, resetCurrentDepartment, fetchAllDepartments } from "../../store/slices/departments";
import { ColumnDef, createColumnHelper, Row } from "@tanstack/react-table";
import { BaseTable } from "@/components/BaseTable/BaseTable";
import { fetchRecruitersByDepartment } from "../../store/slices/vacancy";
import PageLoaderWrapper from "../../components/PageLoaderWrapper";
import { ConfirmDeleteRecruiterModal, MoveRecruiterModal, SelectRecruiterRoleModal, AddRecruiterModal } from "@/components/DepartmentModals";
import { EditIcon, DeleteIcon, ExternalLinkIcon, ArrowBackIcon } from "@chakra-ui/icons";
import { UserRole } from "api/auth/types";
import { selectUserRole } from "@/store/slices/user";
import { DepartmentRecruiter } from "@/model/department";
import { FiPlus } from "react-icons/fi";

const columnHelper = createColumnHelper<DepartmentRecruiter>();

const PostCell = ({ row }: { row: Row<any> }) => {
  let result: string = "";
  switch (row.original.role) {
    case UserRole.RECRUITER:
      result = "Рекрутер";
      break;
    case UserRole.ADMIN:
      result = "Администратор";
      break;
    case UserRole.ACCOUNT_MANAGER:
      result = "Аккаунт-менеджер";
      break;
    case UserRole.DELIVERY_MANAGER:
      result = "Деливери-менеджер";
      break;
    case UserRole.HRD:
      result = "HR директор";
      break;
    case UserRole.DEPARTMENT_MANAGER:
      result = "Руководитель подразделения";
      break;
  }
  return <Text>{result}</Text>;
}
export const SingleDeparmentPage = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const role = useAppSelector(selectUserRole);

  const { currentDepartment, isLoading, departments } = useAppSelector((state) => state.departments);
  const { isOpen: isMoveModalOpen, onOpen: onMoveModalOpen, onClose: onMoveModalClose } = useDisclosure();
  const { isOpen: isAddModalOpen, onOpen: onAddModalOpen, onClose: onAddModalClose } = useDisclosure();
  const [selectedRecruiter, setSelectedRecruiter] = useState<DepartmentRecruiter | null>(null);
  const [targetDepartmentId, setTargetDepartmentId] = useState<number | null>(null);
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isRoleOpen, onOpen: onRoleOpen, onClose: onRoleClose } = useDisclosure();
  const { isOpen: isMoveToDepartmentOpen, onOpen: onMoveToDepartmentOpen, onClose: onMoveToDepartmentClose } = useDisclosure();

  useEffect(() => {
    if (id) {
      dispatch(fetchSingleDepartment(Number(id)));
      dispatch(fetchAllDepartments());
    }
  }, [dispatch, id]);

  useEffect(() => {
    return () => {
      dispatch(resetCurrentDepartment());
    };
  }, [dispatch]);

  const handleMoveRecruiter = async () => {
    if (selectedRecruiter && targetDepartmentId && currentDepartment?.id) {
      dispatch(
        addRecruiter({
          param: {
            department_id: currentDepartment.id,
          },
          data: {
            recruiter_id: selectedRecruiter.id,
          },
        }),
      ).finally(() => {
        dispatch(fetchSingleDepartment(currentDepartment.id));
        onMoveModalClose();
      });
    }
  };

  const rolesOptions = [
    { value: UserRole.RECRUITER, label: "Рекрутер" },
    { value: UserRole.DEPARTMENT_MANAGER, label: "Руководитель подразделения" },
    { value: UserRole.ACCOUNT_MANAGER, label: "Аккаунт-менеджер" },
    { value: UserRole.DELIVERY_MANAGER, label: "Деливери-менеджер" },
    { value: UserRole.HRD, label: "HRD" },
    { value: UserRole.ADMIN, label: "Админ" },
  ];

  const ActionCell = ({ row }: { row: Row<any> }) => {
    return (
      <Flex gap="8px">
        {role === UserRole.ADMIN && (
          <IconButton
            aria-label="Выбрать роль"
            variant="ghost"
            size="sm"
            icon={<EditIcon />}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRecruiter(row.original as DepartmentRecruiter);
              onRoleOpen();
            }}
          />
        )}
        {currentDepartment?.canRemoveRecruiter && (
          <IconButton
            aria-label="Удалить"
            variant="ghost"
            colorScheme="red"
            size="sm"
            icon={<DeleteIcon />}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRecruiter(row.original as DepartmentRecruiter);
              onDeleteOpen();
            }}
          />
        )}
        {currentDepartment?.canMoveRecruiter && (
          <IconButton
            aria-label="Переместить"
            variant="ghost"
            size="sm"
            icon={<ExternalLinkIcon />}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRecruiter(row.original as DepartmentRecruiter);
              onMoveToDepartmentOpen();
            }}
          />
        )}
      </Flex>
    );
  };

  const columns = useMemo<Array<ColumnDef<DepartmentRecruiter, any>>>(
    () => [
      columnHelper.accessor("name", {
        header: "Сотрудник",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("post", {
        header: "Роль",
        cell: (info) => <PostCell row={info.row} />,
      }),
      columnHelper.accessor("email", {
        header: "Контакты",
        cell: (info) => info.getValue(),
      }),
      columnHelper.display({
        id: "action",
        header: "Действия",
        cell: (info) => <ActionCell row={info.row} />,
      }),
    ],
    [ActionCell],
  );

  return (
    <Layout>
      <PageLoaderWrapper isLoading={isLoading} loadingText="Загрузка данных подразделения...">
        {currentDepartment ? (
          <Flex flexDirection="column" maxH="90vh" overflowY="auto" gap="16px">
            <Flex alignItems="center" gap="12px">
              <Button as={RouterLink} to="/departments" variant="ghost" leftIcon={<ArrowBackIcon />}>
                Назад
              </Button>
              <Text fontSize={27} fontWeight={500}>
                Подразделение: {currentDepartment?.name}
              </Text>
            </Flex>
            <Flex justifyContent="space-between" alignItems="center" paddingRight="16px">
              <Text fontSize={16} fontWeight={500}>
                Информация о подразделении
              </Text>
              {currentDepartment?.canAddFreeRecruiter && (
                <Button
                  isDisabled={!currentDepartment?.canAddFreeRecruiter}
                  onClick={onAddModalOpen}
                  bg="#2563eb"
                  color="white"
                  _hover={{ bg: "#2563eb" }}
                  _active={{ bg: "#2563eb" }}
                  leftIcon={<Icon as={FiPlus as any} boxSize={4} />}
                >
                  Добавить рекрутера
                </Button>
              )}
            </Flex>
            <BaseTable list={currentDepartment?.recruiters ?? []} columns={columns} isLoading={isLoading} withoutNavigate={true} />
          </Flex>
        ) : (
          <Flex gap="64px" textAlign="left" flexDirection="column" height="100%">
            <Text w="100%" fontSize={27} fontWeight={500}>
              Подразделение не найдено
            </Text>
          </Flex>
        )}
      </PageLoaderWrapper>

      <Modal isOpen={isMoveModalOpen} onClose={onMoveModalClose}>
        <ModalOverlay backdropFilter="blur(3px)" />
        <ModalContent maxW="50vw" gap="24px" px="24px" paddingTop="32px" paddingBottom="24px">
          <ModalHeader p={0}>Перемещение рекрутера</ModalHeader>
          <ModalBody>
            <Text mb={4}>Выберите подразделение для перемещения рекрутера {selectedRecruiter?.name}</Text>
            <ChakraSelect
              placeholder="Выберите подразделение"
              value={targetDepartmentId || ""}
              onChange={(e) => setTargetDepartmentId(Number(e.target.value))}
              mb={4}
            >
              {departments.items.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </ChakraSelect>
            <Button onClick={handleMoveRecruiter} isDisabled={!targetDepartmentId}>
              Переместить
            </Button>
          </ModalBody>
        </ModalContent>
      </Modal>

      <AddRecruiterModal isOpen={isAddModalOpen} onClose={onAddModalClose} />

      <ConfirmDeleteRecruiterModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        recruiterFullName={selectedRecruiter?.name || ""}
        recruiterId={selectedRecruiter?.id}
      />

      <SelectRecruiterRoleModal
        isOpen={isRoleOpen}
        onClose={onRoleClose}
        onSave={() => onRoleClose()}
        recruiterFullName={selectedRecruiter?.name || ""}
        recruiterId={selectedRecruiter?.id || 0}
        roles={rolesOptions}
        defaultRole={selectedRecruiter?.role}
      />

      <MoveRecruiterModal
        isOpen={isMoveToDepartmentOpen}
        onClose={onMoveToDepartmentClose}
        onMove={() => onMoveToDepartmentClose()}
        recruiterFullName={selectedRecruiter?.name || ""}
        recruiterId={selectedRecruiter?.id || 0}
      />
    </Layout>
  );
};
